import { IconActivity, IconArrowRight } from '@developerz/ui';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { orgHref } from '../lib/org';
import { type RepoRow, repoEnableCall } from '../lib/repos';
import { UNSET } from '../lib/unset';
import { CopyAsCall } from './CopyAsCall';
import { DataTable, type DataTableColumn } from './DataTable';
import { PolicyStatusBadge } from './PolicyStatusBadge';
import { PolicyWarningsBadge } from './PolicyWarningsBadge';
import { StatusPill } from './StatusPill';
import { Button, buttonClass } from './ui/Button';

// The repos grid — one row per repo (spec §3.15): mono name link, policy +
// enabled status pills, muted last-activity date, right-aligned ghost actions;
// and the "no matches" fallback for a search that narrowed to nothing.
//
// Extracted from `routes/Repos` (#2540) when that file crossed the 150-LOC
// ceiling: the route is the CONTAINER (fetch, filter state, toggle state, the
// stat band, the modal) and this is the one coherent presentational block inside
// it. Nothing here fetches, filters or sorts — the rows arrive already narrowed
// by `createRepoFilters`, so this stays a pure paint.
//
// It takes the SAME rows the enabled-only surfaces use. The grid feeds it the
// widened `?include=all` set, which is how a repo that is off — discovered
// dormant, or switched off by a maintainer — reaches a screen with an Enable
// control at all.
//
// A DISABLED ROW LINKS NOWHERE (plan 101 slice 17 row 17-12). Every read behind
// `/repos/<name>` is enabled-only (the API's single-repo gate mirrors the
// enabled list), so a link from a disabled row opened 404 panels whose Retry
// could never succeed. The name is text and the two links are withheld; the
// row's Enable control is the way forward.

export type RepoTableProps = {
  /** Already filtered and sorted by the container. */
  readonly rows: readonly RepoRow[];
  /**
   * Owner-only enable/disable (#972) — rendered iff passed, the same contract
   * as `KeyCard.onVerify`: the container withholds it from a role that would
   * plainly 403 (and while `/v1/me` is still in flight).
   */
  readonly onToggle?: ((repo: RepoRow) => void) | undefined;
  /** Is THIS repo's PATCH in flight — keyed by `full_name`, like the container. */
  readonly isToggling: (fullName: string) => boolean;
};

function formatActivity(iso: string | null): string {
  return iso ? iso.slice(0, 10) : UNSET;
}

// Slashes in `owner/repo` aren't consumed by route param matchers, so we
// percent-encode here and decode in RepoDetail's header for display.
const encoded = (repo: RepoRow): string => encodeURIComponent(repo.full_name);
const detailHref = (repo: RepoRow): string => orgHref(`/repos/${encoded(repo)}`);

export function RepoTable(props: RepoTableProps): JSX.Element {
  const actions = (repo: RepoRow): JSX.Element => (
    <div class="data-table__actions">
      <Show when={repo.enabled}>
        <a class={buttonClass({ variant: 'ghost', size: 'sm' })} href={detailHref(repo)}>
          {t('repos.view_detail')}
          <IconArrowRight size="0.875rem" />
        </a>
        <a
          class={buttonClass({ variant: 'ghost', size: 'sm' })}
          href={orgHref(`/repos/${encoded(repo)}/audit`)}
        >
          <IconActivity size="0.875rem" />
          {t('repos.view_audit')}
        </a>
      </Show>
      <Show when={props.onToggle}>
        {(onToggle) => (
          <>
            <Button
              variant="ghost"
              size="sm"
              disabled={props.isToggling(repo.full_name)}
              onClick={() => onToggle()(repo)}
            >
              {repo.enabled ? t('repos.disable') : t('repos.enable')}
            </Button>
            {/* Offered only where the toggle is: a call the caller would be
                403'd on is not an equivalent of anything on this screen. */}
            <CopyAsCall
              calls={[
                {
                  label: repo.enabled ? t('repos.disable') : t('repos.enable'),
                  call: repoEnableCall(repo, !repo.enabled),
                },
              ]}
            />
          </>
        )}
      </Show>
    </div>
  );

  const columns: readonly DataTableColumn<RepoRow>[] = [
    {
      key: 'repo',
      rowHeader: true,
      header: t('repos.col_repo'),
      mono: true,
      cell: (repo) => (
        <Show when={repo.enabled} fallback={<span>{repo.full_name}</span>}>
          <a href={detailHref(repo)}>{repo.full_name}</a>
        </Show>
      ),
    },
    {
      key: 'status',
      header: t('repos.col_status'),
      cell: (repo) => (
        <div class="repos__badges">
          <PolicyStatusBadge status={repo.policy_status} />
          <StatusPill tone="neutral">
            {repo.enabled ? t('repos.enabled') : t('repos.disabled')}
          </StatusPill>
          <PolicyWarningsBadge count={repo.coderabbit_warnings} />
        </div>
      ),
    },
    {
      key: 'activity',
      header: t('repos.col_activity'),
      class: 'repos__activity',
      cell: (repo) => t('repos.last_activity', { when: formatActivity(repo.last_activity_iso) }),
    },
    {
      key: 'actions',
      header: t('repos.col_actions'),
      align: 'end',
      cell: actions,
    },
  ];

  return (
    <Show when={props.rows.length > 0} fallback={<p class="list-empty">{t('repos.no_matches')}</p>}>
      <DataTable
        label={t('repos.table_label')}
        columns={columns}
        rows={props.rows}
        rowKey={(repo) => repo.full_name}
      />
    </Show>
  );
}
